// Quote Builder — totals panel.
//
// Two cards:
//   • Retailer view only — list cost, supplier discount, trade cost, profit, margin
//   • Customer quote (ex-VAT) — subtotal, of-which drilling, delivery, VAT,
//     grand total inc VAT
// Plus the "Set Total Profit on this job" controls (£ or % apply, on a
// margin/markup basis toggle) and a per-supplier breakdown.
//
// Source-of-truth: kos-qb-v3.js:3512-3574.

import type { QuoteStore } from "./state";
import type { PageContext } from "../../shell/types";
import { SUPPLIERS } from "./types";
import { escapeHtml, fmtPence, svgIcon } from "./formatters";
import {
  distributeMarginPct, distributeMarkupPct, distributeProfitGbp, resetProfitTarget,
  marginPctFromMarkup, markupPctFromMargin, quoteTotals,
} from "./pricing";
import { drillingBySupplier, drillingHoleCount, drillingRetailPence } from "./drilling-aggregate";

// Verbatim source — chevron used in the totals header (kos-qb-v3.js:3517).
const CHEV_ICON = "M6 9l6 6 6-6";

// Transient UI flag — mirrors source's `UI.totalsCollapsed` (kos-qb-v3.js:3513).
// Not persisted: matches source behaviour where collapse resets on reload.
let totalsCollapsed = false;

// ── Which basis the profit figures are shown and entered on ────────────────
//
// MARGIN is profit as a share of RETAIL. MARKUP is profit as a share of TRADE
// COST. They are not interchangeable — 35% margin is 53.8% markup on the same
// job — and the panel used to show one of each without saying so: a "Margin"
// row in the retailer card, next to rows labelled "Markup %", with a bare "%"
// box in the profit distributor that quietly meant margin.
//
// Presentation and INPUT only. Nothing here changes what is stored: the
// distributors both end in distributeProfitGbp, which writes £-markup per line
// exactly as before. The toggle decides which basis the retailer's number is
// read as, and both figures are always on screen so switching can never look
// like the job got more profitable.
//
// Transient like totalsCollapsed — a display preference, not quote data, so it
// is not in the store and never reaches localStorage or PG.
type ProfitBasis = "margin" | "markup";
let profitBasis: ProfitBasis = "margin";

export function mountTotals(host: HTMLElement, store: QuoteStore, ctx: PageContext): () => void {
  function render(): void {
    const q = store.get();
    const t = quoteTotals(q);
    // Drilling is a LINE ITEM, so it is already inside `retailSubPence`.
    // Steve asked for it "next to Delivery"; put an addend there and the
    // customer card stops adding up, because delivery is NOT a line item and
    // drilling is. So it reads as a breakdown OF the subtotal, indented
    // directly beneath it, which answers the same question ("what is the
    // drilling costing on this job?") without inventing £94 of quote.
    const drillPence = drillingRetailPence(q);
    const drillHoles = drillingHoleCount(q);
    const drillBySup = drillingBySupplier(q);
    const isMarkup = profitBasis === "markup";
    // The same profit expressed on the other base. Derived from the margin
    // rather than recomputed from trade so the two rows can never disagree.
    const markupPct = markupPctFromMargin(t.marginPct);
    // Line items store the supplier label ('Trade Kitchens by DTN',
    // 'Custom') in `supplier`, not the tab id. Match on supplierKey first,
    // fall back to tab id for legacy lines stored under the tab name.
    const supColour = (key: string): string =>
      SUPPLIERS.find((s) => s.supplierKey === key || s.key === key)?.colour ?? "#71717a";

    host.innerHTML = `
      <section class="qb-totals${totalsCollapsed ? " is-collapsed" : ""}" aria-label="Totals">
        <header class="qb-totals__head" data-role="totals-toggle" role="button" tabindex="0" aria-expanded="${totalsCollapsed ? "false" : "true"}">
          <h3>Totals</h3>
          <div class="qb-totals__head-right">
            <span class="qb-totals__line-count">${t.lineCount} line${t.lineCount === 1 ? "" : "s"} · trade ${fmtPence(t.tradeCostPence)} · profit ${fmtPence(t.profitPence)} (${t.marginPct.toFixed(1)}%)</span>
            <span class="qb-totals__grand">${fmtPence(t.grandTotalPence)} inc VAT</span>
            <span class="qb-totals__chev">${svgIcon(CHEV_ICON)}</span>
          </div>
        </header>

        <div class="qb-totals__body">
        <div class="qb-totals__grid">
          <article class="qb-totals__card qb-totals__card--retailer">
            <div class="qb-totals__label">Retailer view only</div>
            ${t.listCostPence > 0 ? `<div class="qb-totals__row"><span>List cost</span><strong>${fmtPence(t.listCostPence)}</strong></div>` : ""}
            ${t.discountPence > 0 ? `<div class="qb-totals__row qb-totals__row--green"><span>Supplier discount</span><strong>−${fmtPence(t.discountPence)}</strong></div>` : ""}
            <div class="qb-totals__row qb-totals__row--amber"><span>Your cost (trade)</span><strong>${fmtPence(t.tradeCostPence)}</strong></div>
            <div class="qb-totals__row qb-totals__row--green"><span>Profit</span><strong>${fmtPence(t.profitPence)}</strong></div>
            <div class="qb-totals__row qb-totals__row--green">
              <span>${isMarkup ? "Markup" : "Margin"} <button type="button" class="qb-basis-swap" data-role="basis-swap" title="${isMarkup ? "Show margin (% of retail) instead" : "Show markup (% of trade cost) instead"}">${isMarkup ? "on trade cost" : "of retail"}</button></span>
              <strong>${(isMarkup ? markupPct : t.marginPct).toFixed(1)}%</strong>
            </div>
            <div class="qb-totals__row qb-totals__row--breakdown"><span>${isMarkup ? "= margin of retail" : "= markup on trade cost"}</span><strong>${(isMarkup ? t.marginPct : markupPct).toFixed(1)}%</strong></div>
          </article>

          <article class="qb-totals__card qb-totals__card--customer">
            <div class="qb-totals__label">Customer quote (ex-VAT)</div>
            <div class="qb-totals__row"><span>Subtotal</span><strong>${fmtPence(t.retailSubPence)}</strong></div>
            ${drillPence > 0 ? `<div class="qb-totals__row qb-totals__row--breakdown"><span>of which hinge drilling${drillHoles > 0 ? ` (${drillHoles} hole${drillHoles === 1 ? "" : "s"})` : ""}</span><strong>${fmtPence(drillPence)}</strong></div>` : ""}
            ${t.deliveryPence > 0 ? `<div class="qb-totals__row"><span>Delivery (ex VAT)</span><strong>${fmtPence(t.deliveryPence)}</strong></div>` : ""}
            <div class="qb-totals__row"><span>VAT (${q.vatRate}%)</span><strong>${fmtPence(t.vatAmountPence + t.deliveryVatPence)}</strong></div>
            <div class="qb-totals__row qb-totals__row--grand"><span>Grand total inc VAT</span><strong>${fmtPence(t.grandTotalPence)}</strong></div>
          </article>
        </div>

        <div class="qb-totals__distributor">
          <div class="qb-totals__dist-head">
            <span class="qb-totals__dist-label">Set total profit on this job</span>
            <span class="qb-totals__dist-hint">Spread proportionally by trade cost — overrides per-row markup. The % box is
              <strong>${isMarkup ? "MARKUP, on trade cost" : "MARGIN, of retail"}</strong>.</span>
          </div>
          <div class="qb-totals__dist-row">
            <label>£
              <input type="number" min="0" step="100" placeholder="e.g. 3000" data-role="dist-gbp" />
              <button type="button" class="qb-btn qb-btn--primary" data-role="apply-gbp">Apply</button>
            </label>
            <span class="qb-totals__or">OR</span>
            <label>${isMarkup ? "Markup %" : "Margin %"}
              <input type="number" min="0" ${isMarkup ? "" : 'max="95" '}step="0.5" placeholder="e.g. 35" data-role="dist-pct" />
              <button type="button" class="qb-btn qb-btn--primary" data-role="apply-pct">Apply</button>
            </label>
            <button type="button" class="qb-btn qb-btn--ghost" data-role="basis-swap"
              title="Switch the % box between margin (of retail) and markup (on trade cost)">Use ${isMarkup ? "margin %" : "markup %"}</button>
            <button type="button" class="qb-btn qb-btn--ghost" data-role="reset-profit"
              title="Reset every line's margin back to before the profit target was applied">Reset</button>
          </div>
        </div>

        ${Object.keys(t.bySupplier).length === 0 ? "" : `
          <div class="qb-totals__by-supplier">
            <div class="qb-totals__label">Per-supplier breakdown</div>
            <table>
              <thead>
                <tr><th>Supplier</th><th>Items</th><th>Trade</th><th>Retail</th></tr>
              </thead>
              <tbody>
                ${Object.entries(t.bySupplier)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([sup, v]) => {
                    // Products and drilling as SEPARATE rows. Drilling used to
                    // sit inside Items/Trade/Retail, and an aggregated drilling
                    // row's qty is its hole count — 56 holes read as 56 items.
                    // The two rows still add up to the supplier's total, so
                    // the table reconciles to the Totals card as before.
                    const d = drillBySup[sup];
                    return `
                    <tr>
                      <td><span class="qb-supplier-pill" style="--qb-supplier-colour:${supColour(sup)}">${escapeHtml(sup)}</span></td>
                      <td>${v.qty - (d?.qty ?? 0)}</td>
                      <td>${fmtPence(v.tradePence - (d?.tradePence ?? 0))}</td>
                      <td>${fmtPence(v.retailPence - (d?.retailPence ?? 0))}</td>
                    </tr>
                    ${d ? `
                    <tr class="qb-totals__drill-row" data-role="supplier-drilling">
                      <td>+ Hinge drilling</td>
                      <td>${d.holes > 0 ? `${d.holes} hole${d.holes === 1 ? "" : "s"}` : "—"}</td>
                      <td>${fmtPence(d.tradePence)}</td>
                      <td>${fmtPence(d.retailPence)}</td>
                    </tr>` : ""}`;
                  }).join("")}
              </tbody>
            </table>
          </div>
        `}
        </div>
      </section>
    `;

    const toggleEl = host.querySelector<HTMLElement>("[data-role='totals-toggle']");
    const sectionEl = host.querySelector<HTMLElement>(".qb-totals");
    const toggle = (): void => {
      totalsCollapsed = !totalsCollapsed;
      if (sectionEl) sectionEl.classList.toggle("is-collapsed", totalsCollapsed);
      if (toggleEl) toggleEl.setAttribute("aria-expanded", totalsCollapsed ? "false" : "true");
    };
    toggleEl?.addEventListener("click", toggle);
    toggleEl?.addEventListener("keydown", (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === "Enter" || k === " ") { e.preventDefault(); toggle(); }
    });

    host.querySelector<HTMLButtonElement>("[data-role='apply-gbp']")?.addEventListener("click", () => {
      const inp = host.querySelector<HTMLInputElement>("[data-role='dist-gbp']");
      const v = parseFloat(inp?.value ?? "");
      if (!Number.isFinite(v) || v < 0) {
        ctx.showToast("Enter a £ amount", "error");
        return;
      }
      store.set((d) => { distributeProfitGbp(d, v); });
      ctx.showToast(`Profit distributed: £${v.toLocaleString("en-GB")}`, "success");
      render();
    });
    host.querySelector<HTMLButtonElement>("[data-role='apply-pct']")?.addEventListener("click", () => {
      const inp = host.querySelector<HTMLInputElement>("[data-role='dist-pct']");
      const v = parseFloat(inp?.value ?? "");
      if (isMarkup) {
        // No 99% ceiling on markup: 150% markup is an ordinary retail
        // instruction (it is a 60% margin). The ceiling on the margin side is
        // real — 100% margin means infinite markup.
        if (!Number.isFinite(v) || v <= 0) {
          ctx.showToast("Enter a markup % above 0", "error");
          return;
        }
        store.set((d) => { distributeMarkupPct(d, v); });
        // State the other basis in the confirmation so the number the
        // retailer typed is never silently reinterpreted.
        ctx.showToast(
          `Markup distributed: ${v}% on trade cost (= ${marginPctFromMarkup(v).toFixed(1)}% margin)`,
          "success",
        );
        render();
        return;
      }
      if (!Number.isFinite(v) || v <= 0 || v >= 99) {
        ctx.showToast("Enter a margin % between 0 and 99", "error");
        return;
      }
      store.set((d) => { distributeMarginPct(d, v); });
      ctx.showToast(
        `Margin distributed: ${v}% of retail (= ${markupPctFromMargin(v).toFixed(1)}% markup)`,
        "success",
      );
      render();
    });
    // Both swap controls (the card's inline one and the distributor button)
    // flip the same flag. Display only — the quote is not touched, so this
    // takes no store.set and cannot mark the quote dirty.
    host.querySelectorAll<HTMLButtonElement>("[data-role='basis-swap']").forEach((btn) => {
      btn.addEventListener("click", () => {
        profitBasis = isMarkup ? "margin" : "markup";
        render();
      });
    });
    host.querySelector<HTMLButtonElement>("[data-role='reset-profit']")?.addEventListener("click", () => {
      let didReset = false;
      store.set((d) => { didReset = resetProfitTarget(d); });
      ctx.showToast(
        didReset ? "Line margins reset to before the profit target" : "Nothing to reset",
        didReset ? "success" : "info",
      );
      render();
    });
  }

  render();
  const unsub = store.subscribe(() => render());
  return () => unsub();
}
